import type {
  DndContextProps,
  DragCancelEvent,
  DragEndEvent,
  DragStartEvent,
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  SortableContext,
  type SortableContextProps,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type SlotProps } from '@radix-ui/react-slot'
import * as React from 'react'

import { composeRefs } from '../../../utils/misc/composeRefs'
import { DragButton, StyledSortableItem } from './Sortable.styles'

export type SortableOrientation = 'vertical' | 'horizontal' | 'mixed'

type OrientationConfig = {
  modifiers: DndContextProps['modifiers']
  strategy: SortableContextProps['strategy']
}

const orientationConfig: Record<SortableOrientation, OrientationConfig> = {
  vertical: {
    modifiers: [restrictToVerticalAxis, restrictToParentElement],
    strategy: verticalListSortingStrategy,
  },
  horizontal: {
    modifiers: [restrictToHorizontalAxis, restrictToParentElement],
    strategy: horizontalListSortingStrategy,
  },
  mixed: {
    modifiers: [restrictToParentElement],
    strategy: undefined,
  },
}

interface SortableProps<TData extends { id: UniqueIdentifier }> extends DndContextProps {
  value: TData[]
  onValueChange?: (items: TData[]) => void
  onMove?: (event: { activeIndex: number; overIndex: number }) => void
  collisionDetection?: DndContextProps['collisionDetection']
  modifiers?: DndContextProps['modifiers']
  strategy?: SortableContextProps['strategy']
  orientation?: SortableOrientation
  overlay?: React.ReactNode | null
}

const Sortable = <TData extends { id: UniqueIdentifier }>({
  value,
  onValueChange,
  onDragStart,
  onDragEnd,
  onDragCancel,
  collisionDetection = closestCenter,
  modifiers,
  strategy,
  onMove,
  orientation = 'vertical',
  overlay,
  children,
  ...props
}: SortableProps<TData>) => {
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor)
  )

  const config = orientationConfig[orientation]

  const handleDragStart = (event: DragStartEvent) => {
    onDragStart?.(event)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const activeIndex = value.findIndex(item => item.id === active.id)
      const overIndex = value.findIndex(item => item.id === over.id)

      if (onMove) {
        onMove({ activeIndex, overIndex })
      } else {
        onValueChange?.(arrayMove(value, activeIndex, overIndex))
      }
    }

    onDragEnd?.(event)
  }

  const handleDragCancel = (event: DragCancelEvent) => {
    onDragCancel?.(event)
  }

  return (
    <DndContext
      modifiers={modifiers ?? config.modifiers}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      collisionDetection={collisionDetection}
      {...props}
    >
      <SortableContext items={value} strategy={strategy ?? config.strategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

interface SortableItemContextProps {
  attributes: React.HTMLAttributes<HTMLElement>
  listeners: DraggableSyntheticListeners | undefined
  isDragging?: boolean
}

const SortableItemContext = React.createContext<SortableItemContextProps | undefined>({
  attributes: {},
  listeners: undefined,
  isDragging: false,
})

function useSortableItem() {
  const context = React.useContext(SortableItemContext)

  if (context === undefined) {
    throw new Error('useSortableItem must be used within a SortableItem')
  }

  return context
}

interface SortableItemProps extends SlotProps {
  value: UniqueIdentifier
  asTrigger?: boolean
  asChild?: boolean
}

const SortableItem = React.forwardRef<HTMLDivElement, SortableItemProps>(
  ({ value, asTrigger, asChild, className, ...props }, ref) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: value,
    })

    const context = React.useMemo<SortableItemContextProps>(
      () => ({ attributes, listeners, isDragging }),
      [attributes, listeners, isDragging]
    )
    const style: React.CSSProperties = {
      opacity: isDragging ? 0.5 : 1,
      transform: CSS.Translate.toString(transform),
      transition,
    }

    return (
      <SortableItemContext.Provider value={context}>
        <StyledSortableItem
          asChild={asChild}
          data-state={isDragging ? 'dragging' : undefined}
          ref={composeRefs(ref, setNodeRef as React.Ref<HTMLDivElement>)}
          style={style}
          isDragging={isDragging}
          asTrigger={asTrigger}
          {...(asTrigger ? attributes : {})}
          {...(asTrigger ? listeners : {})}
          {...props}
        />
      </SortableItemContext.Provider>
    )
  }
)
SortableItem.displayName = 'SortableItem'

interface SortableDragHandleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  withHandle?: boolean
}

const SortableDragHandle = React.forwardRef<HTMLButtonElement, SortableDragHandleProps>(
  ({ className, ...props }, ref) => {
    const { attributes, listeners, isDragging } = useSortableItem()

    return (
      <DragButton
        ref={ref}
        data-state={isDragging ? 'dragging' : undefined}
        {...attributes}
        {...listeners}
        {...props}
      />
    )
  }
)
SortableDragHandle.displayName = 'SortableDragHandle'

export { Sortable, SortableDragHandle, SortableItem }
